import { SeededRng } from "./rng";
import {
  ARENA_HEIGHT,
  ARENA_WIDTH,
  type ArenaMap,
  type Cell,
  type MapGenerationOptions,
  TileType,
  type TileType as TileKind,
  type Seed,
} from "./types";

export interface MutableArenaMap {
  width: number;
  height: number;
  tiles: TileKind[][];
}

const CARDINALS: readonly Cell[] = Object.freeze([
  Object.freeze({ x: 0, y: -1 }),
  Object.freeze({ x: 0, y: 1 }),
  Object.freeze({ x: -1, y: 0 }),
  Object.freeze({ x: 1, y: 0 }),
]);

export function cellKey(cell: Cell): string {
  return `${cell.x},${cell.y}`;
}

export function parseCellKey(key: string): Cell {
  const separator = key.indexOf(",");
  if (separator < 1) {
    throw new TypeError(`Invalid cell key: ${key}`);
  }
  const x = Number(key.slice(0, separator));
  const y = Number(key.slice(separator + 1));
  if (!Number.isInteger(x) || !Number.isInteger(y)) {
    throw new TypeError(`Invalid cell key: ${key}`);
  }
  return { x, y };
}

export function isInside(
  map: Pick<ArenaMap, "width" | "height">,
  cell: Cell,
): boolean {
  return (
    Number.isInteger(cell.x) &&
    Number.isInteger(cell.y) &&
    cell.x >= 0 &&
    cell.y >= 0 &&
    cell.x < map.width &&
    cell.y < map.height
  );
}

export function getTile(map: ArenaMap, cell: Cell): TileKind | undefined {
  if (!isInside(map, cell)) {
    return undefined;
  }
  return map.tiles[cell.y]?.[cell.x];
}

export function isWalkableTile(tile: TileKind | undefined): boolean {
  return tile === TileType.Floor;
}

export function neighbors(cell: Cell): readonly Cell[] {
  return CARDINALS.map((direction) => ({
    x: cell.x + direction.x,
    y: cell.y + direction.y,
  }));
}

export function cloneArenaMap(map: ArenaMap): MutableArenaMap {
  const violations = validateArenaMap(map);
  if (violations.length > 0) {
    throw new RangeError(violations.join("; "));
  }
  return {
    width: map.width,
    height: map.height,
    tiles: map.tiles.map((row) => [...row]),
  };
}

export function freezeArenaMap(map: MutableArenaMap | ArenaMap): ArenaMap {
  return {
    width: map.width,
    height: map.height,
    tiles: map.tiles.map((row) => Object.freeze([...row])),
  };
}

export function validateArenaMap(map: ArenaMap): readonly string[] {
  const errors: string[] = [];
  if (!Number.isInteger(map.width) || map.width < 3) {
    errors.push("map width must be an integer of at least 3");
  }
  if (!Number.isInteger(map.height) || map.height < 3) {
    errors.push("map height must be an integer of at least 3");
  }
  if (!Array.isArray(map.tiles) || map.tiles.length !== map.height) {
    errors.push("map tiles must contain one row per height");
    return errors;
  }

  for (let y = 0; y < map.height; y += 1) {
    const row = map.tiles[y];
    if (!Array.isArray(row) || row.length !== map.width) {
      errors.push(`map row ${y} must contain one tile per width`);
      continue;
    }
    for (let x = 0; x < map.width; x += 1) {
      const tile = row[x];
      if (
        tile !== TileType.Floor &&
        tile !== TileType.Indestructible &&
        tile !== TileType.Destructible
      ) {
        errors.push(`map tile ${x},${y} has an unknown type`);
      }
    }
  }
  return errors;
}

export function mapFromRows(rows: readonly string[]): ArenaMap {
  if (rows.length < 3) {
    throw new RangeError("A map needs at least three rows");
  }
  const width = rows[0]?.length ?? 0;
  if (width < 3 || rows.some((row) => row.length !== width)) {
    throw new RangeError("Map rows must have a shared width of at least three");
  }

  const tiles: TileKind[][] = rows.map((row, y) =>
    [...row].map((character, x) => {
      switch (character) {
        case ".":
        case "0":
        case " ":
          return TileType.Floor;
        case "#":
        case "X":
          return TileType.Indestructible;
        case "+":
        case "B":
        case "?":
          return TileType.Destructible;
        default:
          throw new RangeError(
            `Unknown map character ${JSON.stringify(character)} at ${x},${y}`,
          );
      }
    }),
  );

  return freezeArenaMap({ width, height: rows.length, tiles });
}

export function mapToRows(map: ArenaMap): readonly string[] {
  return map.tiles.map((row) =>
    row
      .map((tile) => {
        if (tile === TileType.Indestructible) {
          return "#";
        }
        if (tile === TileType.Destructible) {
          return "+";
        }
        return ".";
      })
      .join(""),
  );
}

export function getSpawnPoints(
  width = ARENA_WIDTH,
  height = ARENA_HEIGHT,
  count = 4,
): readonly Cell[] {
  if (count < 1 || !Number.isInteger(count)) {
    throw new RangeError("Spawn count must be a positive integer");
  }
  const candidates: Cell[] = [
    { x: 1, y: 1 },
    { x: width - 2, y: height - 2 },
    { x: width - 2, y: 1 },
    { x: 1, y: height - 2 },
    { x: 3, y: 1 },
    { x: width - 4, y: height - 2 },
    { x: width - 4, y: 1 },
    { x: 3, y: height - 2 },
  ];

  const unique: Cell[] = [];
  const seen = new Set<string>();
  for (const candidate of candidates) {
    if (
      !isInside({ width, height }, candidate) ||
      seen.has(cellKey(candidate))
    ) {
      continue;
    }
    seen.add(cellKey(candidate));
    unique.push(candidate);
  }

  for (let y = 1; unique.length < count && y < height - 1; y += 1) {
    for (let x = 1; unique.length < count && x < width - 1; x += 1) {
      const candidate = { x, y };
      if (!seen.has(cellKey(candidate))) {
        seen.add(cellKey(candidate));
        unique.push(candidate);
      }
    }
  }

  if (unique.length < count) {
    throw new RangeError(
      `Map ${width}x${height} cannot provide ${count} spawn points`,
    );
  }
  return unique;
}

function isSpawnClear(
  cell: Cell,
  spawnPoints: readonly Cell[],
  radius: number,
): boolean {
  return spawnPoints.some(
    (spawn) =>
      Math.abs(spawn.x - cell.x) + Math.abs(spawn.y - cell.y) <= radius,
  );
}

export function generateMap(
  seed: Seed,
  options: MapGenerationOptions = {},
): ArenaMap {
  const width = options.width ?? ARENA_WIDTH;
  const height = options.height ?? ARENA_HEIGHT;
  const playerCount = options.playerCount ?? 4;
  const density = options.destructibleDensity ?? 0.58;
  const clearRadius = options.clearRadius ?? 1;

  if (
    !Number.isInteger(width) ||
    !Number.isInteger(height) ||
    width < 5 ||
    height < 5
  ) {
    throw new RangeError(
      "Generated maps must be at least 5x5 with integer dimensions",
    );
  }
  if (density < 0 || density > 1 || !Number.isFinite(density)) {
    throw new RangeError("destructibleDensity must be between 0 and 1");
  }
  if (!Number.isInteger(playerCount) || playerCount < 1) {
    throw new RangeError("playerCount must be a positive integer");
  }

  const spawnPoints =
    options.spawnPoints ?? getSpawnPoints(width, height, playerCount);
  if (spawnPoints.length < playerCount) {
    throw new RangeError("Not enough spawn points for the requested players");
  }
  const rng = new SeededRng(seed);
  const tiles: TileKind[][] = [];

  for (let y = 0; y < height; y += 1) {
    const row: TileKind[] = [];
    for (let x = 0; x < width; x += 1) {
      const border = x === 0 || y === 0 || x === width - 1 || y === height - 1;
      const structural = x % 2 === 0 && y % 2 === 0;
      if (border || structural) {
        row.push(TileType.Indestructible);
      } else if (isSpawnClear({ x, y }, spawnPoints, clearRadius)) {
        row.push(TileType.Floor);
      } else if (rng.nextFloat() < density) {
        row.push(TileType.Destructible);
      } else {
        row.push(TileType.Floor);
      }
    }
    tiles.push(row);
  }

  return freezeArenaMap({ width, height, tiles });
}

export const createClassicMap = generateMap;
export const generateArenaMap = generateMap;
export const createMapFromRows = mapFromRows;
export const createMap = generateMap;
